import React, { useState, useEffect, useRef } from 'react';
import { BACKEND_URL, DEFAULT_QR_CODE_URL } from '../config';
import { useRefresh } from '../RefreshContext';
import EditItemCheck from './EditItemCheck';
import PrinterSettings from './PrinterSettings';
import PrintTicket from './PrintTicket';
import OrderClose from './OrderClose';
import SplitCheck from './SplitCheck';
import RecombineCheck from './RecombineCheck';
import CombineCheck from './CombineCheck';
import { useLanguage } from '../contexts/LanguageContext';
import { isIOS, canUseBluetooth, printViaIframe } from '../utils/iosPrintHelper';
import { openReceiptPdf } from '../utils/receiptPdfGenerator';
import { useSystemFeatures } from '../hooks/useSystemFeatures';
import { splitTaxInclusive, taxLabel, formatTaxRate } from '../utils/systemFeatures';
import { receiptPaymentLabel, tenderTipTotal } from '../utils/paymentMethods';

function OrderTotal({ orderId, theme, onAddToPreorder, orderName, tableName, onClose, user }) {
    const { t, language } = useLanguage();
    const { taxRate } = useSystemFeatures();

    const [orderItems, setOrderItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [showPrinterSettings, setShowPrinterSettings] = useState(false);
    const [printLoading, setPrintLoading] = useState(false);
    const [showPrintTicket, setShowPrintTicket] = useState(false);
    const [showOrderClose, setShowOrderClose] = useState(false);
    const [showSplitCheck, setShowSplitCheck] = useState(false);
    const [showRecombineCheck, setShowRecombineCheck] = useState(false);
    const [showCombineCheck, setShowCombineCheck] = useState(false);
    const [orderData, setOrderData] = useState(null);
    // The tenders recorded against this order, for the receipt
    const [orderPayments, setOrderPayments] = useState([]);
    const [childOrders, setChildOrders] = useState([]);
    const [parentOrderName, setParentOrderName] = useState('');
    const [showIosPrintOptions, setShowIosPrintOptions] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [showPrinterMapping, setShowPrinterMapping] = useState(false);
    const [printerMappings, setPrinterMappings] = useState([]);
    const [skipPrinterMapping, setSkipPrinterMapping] = useState(
        localStorage.getItem('skipPrinterMapping') === 'true'
    );
    const printerMappingResolveRef = useRef(null);
    const { refreshKey } = useRefresh();
    const printRef = useRef();

    // Specials state
    const [applicableSpecials, setApplicableSpecials] = useState([]);
    const [checkingSpecials, setCheckingSpecials] = useState(false);
    const [selectedSpecial, setSelectedSpecial] = useState(null);
    const [specialDiscount, setSpecialDiscount] = useState(0);
    const [showSpecials, setShowSpecials] = useState(false);

    useEffect(() => {
        fetchOrderItems();
        fetchOrderData();
        fetchOrderPayments();
        fetchChildOrders();
        fetchParentOrderName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId, refreshKey]);

    // Check for applicable specials when order items change
    useEffect(() => {
        if (orderItems.length > 0) {
            checkApplicableSpecials();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderItems]);

    const fetchOrderData = async () => {
        if (!orderId) return;

        try {
            const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}`);
            if (!response.ok) throw new Error(t('error') + ': Failed to fetch order data');
            const data = await response.json();
            setOrderData(data);
        } catch (err) {
            console.error('Error fetching order data:', err);
        }
    };

    // The tenders the receipt names. An order closed before the tender ledger
    // existed carries none, and its receipt prints as it does today.
    const fetchOrderPayments = async () => {
        if (!orderId) return;

        try {
            const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}/payments`);
            if (!response.ok) throw new Error('Failed to fetch order payments');
            const data = await response.json();
            setOrderPayments(data.payments || []);
        } catch (err) {
            console.error('Error fetching order payments:', err);
            setOrderPayments([]);
        }
    };

    const fetchChildOrders = async () => {
        if (!orderId) return;

        try {
            const response = await fetch(`${BACKEND_URL}/api/orders`);
            if (!response.ok) throw new Error('Failed to fetch orders');
            const data = await response.json();
            const children = data.filter(order => order.parent_tab_id === parseInt(orderId) && order.status === 0);
            setChildOrders(children);
        } catch (err) {
            console.error('Error fetching child orders:', err);
            setChildOrders([]);
        }
    };

    const fetchParentOrderName = async () => {
        if (!orderId || !orderData?.parent_tab_id) return;

        try {
            const response = await fetch(`${BACKEND_URL}/api/orders/${orderData.parent_tab_id}`);
            if (!response.ok) throw new Error('Failed to fetch parent order');
            const parentOrder = await response.json();
            if (parentOrder.name) {
                setParentOrderName(parentOrder.name.substring(0, 5));
            }
        } catch (err) {
            console.error('Error fetching parent order name:', err);
            setParentOrderName('');
        }
    };

    const fetchOrderItems = async () => {
        if (!orderId) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(`${BACKEND_URL}/api/order-products?order_id=${orderId}`);
            if (!response.ok) throw new Error(t('error') + ': Failed to fetch order items');
            const data = await response.json();
            setOrderItems(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 0: return t('new');
            case 1: return t('inProgress');
            case 2: return t('readyEnroute');
            case 3: return t('finished');
            default: return t('unknown');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 0: return '#2196f3'; // Blue - New
            case 1: return '#ff9800'; // Orange - In Progress
            case 2: return '#aaaf4cff'; // Green - Enroute
            case 3: return '#05d11dff'; // Green - Finished
            default: return '#666';
        }
    };

    // Calculate discount percent for each item
    const itemsWithDiscountPercent = orderItems.map(item => {
        const price = Number(item.price || 0) * item.quantity;
        const discount = Number(item.discount || 0) * item.quantity;
        let discount_percent = null;
        if (price > 0 && discount > 0) {
            discount_percent = ((discount / price) * 100).toFixed(1);
        }
        return { ...item, discount_percent };
    });

    // Sort items by status priority (2,1,0,3,4) then by creation time
    const sortedItems = [...itemsWithDiscountPercent].sort((a, b) => {
        const statusPriority = { 2: 0, 1: 1, 0: 2, 3: 3, 4: 4 };
        const aPriority = statusPriority[a.status] ?? 5;
        const bPriority = statusPriority[b.status] ?? 5;

        if (aPriority !== bPriority) {
            return aPriority - bPriority;
        }

        // If same status, sort by creation time (oldest first)
        return new Date(a.created || a.item_created) - new Date(b.created || b.item_created);
    });


    // Calculate subtotal (sum of all items before discount)
    const subtotalPrice = orderItems.reduce((sum, item) => {
        return sum + (Number(item.price || 0) * item.quantity);
    }, 0);

    // Calculate total discount (sum of all item discounts - discount is per-unit, multiply by quantity)
    const totalDiscount = orderItems.reduce((sum, item) => {
        return sum + (Number(item.discount || 0) * item.quantity);
    }, 0);

    // Total price after discount
    const totalPrice = subtotalPrice - totalDiscount;

    // Count in-progress items (status 0, 1, or 2)
    // eslint-disable-next-line no-unused-vars
    const inProgressCount = orderItems.filter(item => [0, 1, 2].includes(item.status)).length;

    // Helper function to get description in current language
    const getSpecialDescription = (special) => {
        if (language === 'es' && special.description_español) {
            return special.description_español;
        }
        return special.description;
    };

    const handleReorder = (item) => {
        if (onAddToPreorder) {
            // Create a product object similar to what OrderProducts sends. A
            // custom line goes back out as a custom item with its typed name
            // and price, under a local id of its own so it does not merge with
            // another line on the same placeholder product.
            const product = item.custom_name
                ? {
                    id: `custom-reorder-${item.id}`,
                    custom: true,
                    name: item.custom_name,
                    price: Number(item.price),
                    adjustedPrice: Number(item.price),
                    category_id: item.category_id,
                    category: item.category,
                    area: item.area
                }
                : {
                    id: item.product_id,
                    name: item.product_name,
                    price: item.price,
                    category: item.category,
                    area: item.area
                };

            // Add the item to preorder with the same quantity
            for (let i = 0; i < item.quantity; i++) {
                onAddToPreorder(product);
            }
        }
    };

    // Check for applicable specials
    const checkApplicableSpecials = async () => {
        setCheckingSpecials(true);
        try {
            const response = await fetch(`${BACKEND_URL}/api/specials/check-applicable`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order_items: orderItems })
            });

            if (response.ok) {
                const specials = await response.json();
                setApplicableSpecials(specials);
            }
        } catch (err) {
            console.error('Error checking specials:', err);
        } finally {
            setCheckingSpecials(false);
        }
    };

    // Apply a special discount (calls backend for all types)
    const applySpecial = (special) => {
        if (special.special_type === 'combo_deal') {
            // Call backend to apply combo discount to order items
            const comboItemIds = (special.matching_items || []).map(i => i.id);
            const comboDiscount = special.combo_discount || (special.combo_total - special.combo_price);
            fetch(`${BACKEND_URL}/api/orders/${orderId}/apply-combo-discount`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    combo_special_id: special.id,
                    combo_discount: comboDiscount,
                    combo_item_ids: comboItemIds
                })
            })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setSelectedSpecial(special);
                    setSpecialDiscount(comboDiscount);
                    fetchOrderItems(); // Refresh items to get updated discounts
                } else {
                    alert(t('error') + ': ' + (data.error || 'Failed to apply combo discount'));
                }
            })
            .catch(err => {
                console.error('Error applying combo discount:', err);
                alert(t('error') + ': ' + err.message);
            });
        } else if (
            special.special_type === 'buy_x_get_y_free' ||
            special.special_type === 'percentage_discount' ||
            special.special_type === 'fixed_discount'
        ) {
            // Call backend to apply special discount to order items
            let matchingItemIds;
            if (special.special_type === 'buy_x_get_y_free') {
                                // Only include items whose options do NOT include any excluded option ids
                                const excludedOptionIds = Array.isArray(special.conditions?.exclude_option_ids)
                                    ? special.conditions.exclude_option_ids.map(Number)
                                    : [];
                                matchingItemIds = (special.matching_items || [])
                                    .filter(i => {
                                        let optionIds = [];
                                        if (!i.options) return true;
                                        if (typeof i.options === 'string') {
                                            try { optionIds = JSON.parse(i.options); } catch { return true; }
                                        } else if (Array.isArray(i.options)) {
                                            optionIds = i.options;
                                        }
                                        if (!optionIds || optionIds.length === 0) return true;
                                        // Exclude if ANY option is in the exclusion list
                                        return !optionIds.some(optId => excludedOptionIds.includes(Number(optId)));
                                    })
                                    .map(i => i.id);
            } else {
                matchingItemIds = (special.matching_items || []).map(i => i.id);
            }
            let payload = {
                special_id: special.id,
                special_type: special.special_type,
                matching_item_ids: matchingItemIds
            };
            if (special.special_type === 'buy_x_get_y_free') {
                payload.buy_quantity = special.conditions?.buy_quantity || 0;
                payload.get_quantity = special.conditions?.get_quantity || 0;
            } else {
                payload.discount_value = special.discount_value;
            }
            fetch(`${BACKEND_URL}/api/orders/${orderId}/apply-special-discount`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setSelectedSpecial(special);
                    setSpecialDiscount(data.total_discount || 0);
                    fetchOrderItems(); // Refresh items to get updated discounts
                } else {
                    alert(t('error') + ': ' + (data.error || 'Failed to apply special discount'));
                }
            })
            .catch(err => {
                console.error('Error applying special discount:', err);
                alert(t('error') + ': ' + err.message);
            });
        }
    };

    // Remove applied special and clear all discounts in DB
    const removeSpecial = () => {
        fetch(`${BACKEND_URL}/api/orders/${orderId}/clear-discounts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        })
        .then(res => res.json())
        .then(data => {
            if (data.success) {
                setSelectedSpecial(null);
                setSpecialDiscount(0);
                fetchOrderItems();
            } else {
                alert(t('error') + ': ' + (data.error || 'Failed to clear discounts'));
            }
        })
        .catch(err => {
            console.error('Error clearing discounts:', err);
            alert(t('error') + ': ' + err.message);
        });
    };

    const handlePrint2 = async () => {
        // On iOS, show print options modal (AirPrint, PDF, or Bluefy)
        if (isIOS()) {
            setShowIosPrintOptions(true);
            return;
        }

        setPrintLoading(true);

        try {
            // Load printer settings from API with localStorage fallback
            let settings = null;
            try {
                const response = await fetch(`${BACKEND_URL}/api/printer-settings`);
                if (response.ok) {
                    settings = await response.json();
                } else {
                    console.warn('Could not load settings from API, using localStorage fallback');
                }
            } catch (err) {
                console.warn('Error fetching settings from API:', err);
            }

            // If API fetch failed, use localStorage as fallback
            if (!settings) {
                settings = {};
                try {
                    const savedConfig = localStorage.getItem('printerConfig');
                    if (savedConfig) {
                        const config = JSON.parse(savedConfig);
                        settings.paperWidth = config.width || 32;
                        settings.fontSize = config.fontSize || 'normal';
                    }
                    const savedFormat = localStorage.getItem('receiptFormat');
                    if (savedFormat) {
                        const format = JSON.parse(savedFormat);
                        Object.assign(settings, format);
                    }
                } catch (localErr) {
                    console.warn('Could not load from localStorage:', localErr);
                }
            }

            // Generate receipt content using the same format as test print
            const receipt = generateReceiptForBluetooth(orderItems, orderId, tableName, orderName, orderData?.special_name, settings, orderData?.closed_at, orderPayments);

            // Print via Bluetooth
            await printViaBluetooth(receipt);

            console.log('Receipt printed successfully via Bluetooth');
        } catch (error) {
            console.error('Bluetooth print error:', error);

            // Don't show alert for iOS Bluetooth help (modal is shown instead)
            if (error.message !== 'iOS_BLUETOOTH_NOT_SUPPORTED') {
                // Show user-friendly error message
                let errorMessage = error.message;
                if (error.name === 'NotFoundError') {
                    errorMessage = t('noPrinterSelected');
                } else if (error.name === 'SecurityError') {
                    errorMessage = t('accessDenied');
                } else if (error.name === 'NetworkError') {
                    errorMessage = t('connectionFailed');
                }

                alert(t('printError') + ': ' + errorMessage);
            }
        } finally {
            setPrintLoading(false);
        }
    };

    // Generate ESC/POS receipt for Bluetooth printing
    const generateReceiptForBluetooth = (orderItems, orderId, tableName, orderName, specialName, settings, closedAt, payments = []) => {
        // Use settings with defaults
        const width = settings?.paperWidth || 32;
        const fontSize = settings?.fontSize || 'normal';
        const businessName = settings?.businessName || 'CERVECERIA GIBBONS';
        const address = settings?.address || 'C. ESCORZA 848';
        const taxId = settings?.taxId || 'CGI181112777';
        const footerMessage1 = settings?.footerMessage1 || '';
        const footerMessage2 = settings?.footerMessage2 || '';
        const footerMessage3 = settings?.footerMessage3 || '';
        const showQrCode = settings?.showQrCode || false;
        const qrCodeUrl = settings?.qrCodeUrl || DEFAULT_QR_CODE_URL;
        const qrCodeText = settings?.qrCodeText || 'Escanea para visitar:';
        const includeIva = settings?.includeIva !== false; // default true
        const paperCut = settings?.paperCut || 'partial';

        const separator = '='.repeat(width);
        const dashLine = '-'.repeat(width);

        const leftRight = (left, right, width) => {
            const spaces = Math.max(1, width - left.length - right.length);
            return left + ' '.repeat(spaces) + right;
        };

        let receipt = '\x1B\x40'; // Initialize printer
        receipt += '\x1B\x74\x00'; // Set character code table to CP437

        // Font size commands
        if (fontSize === 'small') {
            receipt += '\x1D\x21\x00'; // Normal size
        } else if (fontSize === 'large') {
            receipt += '\x1D\x21\x11'; // Double height and width
        } else {
            receipt += '\x1D\x21\x01'; // Double height only (normal)
        }

        // Header - centered and bold
        receipt += '\x1B\x61\x01'; // Center align
        receipt += '\x1B\x45\x01'; // Bold on
        receipt += businessName + '\n';
        receipt += '\x1B\x45\x00'; // Bold off
        receipt += address + '\n';
        receipt += taxId + '\n';
        receipt += '\x1B\x61\x00'; // Left align
        receipt += separator + '\n\n';

        // Order info — use closedAt for closed orders, current time for active
        const now = closedAt ? new Date(closedAt) : new Date();
        const dateTime = now.toLocaleString('es-MX', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });

        receipt += `Fecha/Hora: ${dateTime}\n`;
        receipt += `Orden #: ${orderId}\n`;
        receipt += `Mesa: ${tableName || 'N/A'}\n`;
        if (orderName && orderName !== tableName) {
            receipt += `Nombre: ${orderName}\n`;
        }

        // Special/promotion section (if applied)
        if (specialName) {
            receipt += '\x1B\x61\x01'; // Center align
            receipt += '\x1B\x45\x01'; // Bold on
            receipt += '\x1B\x21\x08'; // Double strike
            receipt += '\n--- PROMOCION APLICADA ---\n';
            receipt += `${specialName}\n`;
            receipt += '\x1B\x45\x00'; // Bold off
            receipt += '\x1B\x21\x00'; // Normal
            receipt += '\x1B\x61\x00'; // Left align
        }
        receipt += '\n';

        // Items header
        receipt += dashLine + '\n';
        receipt += 'CANT PRODUCTO             PRECIO\n';
        receipt += dashLine + '\n';

        // Calculate totals
        let total = 0;
        let totalDiscount = 0;

        // Items
        orderItems.forEach(item => {
            const qty = item.quantity;
            const name = item.product_name;
            const price = Number(item.price || 0);
            const discount = Number(item.discount || 0);
            const itemTotal = qty * (price - discount);
            total += itemTotal;

            const qtyStr = qty.toString().padStart(2);
            const maxNameLength = width - 14;
            const truncatedName = name.length > maxNameLength
                ? name.substring(0, maxNameLength - 3) + '...'
                : name;
            const priceStr = `$${itemTotal.toFixed(2)}`.padStart(8);
            receipt += `${qtyStr}x ${truncatedName.padEnd(maxNameLength)}${priceStr}\n`;

            if (discount > 0) {
                // Display total discount for the line (per-unit discount * quantity)
                const lineDiscount = discount * qty;
                totalDiscount += lineDiscount;
                receipt += `    (Descuento: -$${lineDiscount.toFixed(2)})\n`;
            }
        });

        // Totals
        receipt += dashLine + '\n';

        // Show total discount if there is any
        if (totalDiscount > 0) {
            receipt += '\x1B\x45\x01'; // Bold on
            receipt += '\x1B\x21\x08'; // Double strike
            receipt += leftRight('Descuento:', `-$${totalDiscount.toFixed(2)}`, width) + '\n';
            receipt += '\x1B\x45\x00'; // Bold off
            receipt += '\x1B\x21\x00'; // Normal
            receipt += '\n'; // Blank line after discount
        }

        if (includeIva) {
            const { subtotal, tax: ivaAmount } = splitTaxInclusive(total, taxRate);
            receipt += leftRight('Subtotal:', `$${subtotal.toFixed(2)}`, width) + '\n';
            receipt += leftRight(`${taxLabel(taxRate)}:`, `$${ivaAmount.toFixed(2)}`, width) + '\n';
        }
        receipt += '\x1B\x45\x01'; // Bold on
        receipt += leftRight('TOTAL:', `$${total.toFixed(2)}`, width) + '\n';
        receipt += separator + '\n';
        receipt += '\x1B\x45\x00'; // Bold off

        // How the check was paid, one line per tender
        if (payments.length > 0) {
            receipt += 'FORMA DE PAGO\n';
            payments.forEach(payment => {
                receipt += leftRight(`${receiptPaymentLabel(payment.payment_type_id)}:`, `$${Number(payment.amount).toFixed(2)}`, width) + '\n';
            });
            const tipTotal = tenderTipTotal(payments);
            if (tipTotal > 0) {
                receipt += leftRight('Propina:', `$${tipTotal.toFixed(2)}`, width) + '\n';
            }
            receipt += separator + '\n';
        }

        receipt += '\n';


        // Footer - centered
        receipt += '\x1B\x61\x01'; // Center align
        if (footerMessage1) receipt += footerMessage1 + '\n';
        if (footerMessage2) receipt += footerMessage2 + '\n';
        if (footerMessage1 || footerMessage2) receipt += '\n'; // Add blank line if there were footer messages
        if (footerMessage3) receipt += footerMessage3 + '\n';

        // QR Code (if enabled)
        if (showQrCode && qrCodeUrl) {
            receipt += '\n';
            const qrDataLength = qrCodeUrl.length + 3;
            const pL = qrDataLength % 256;
            const pH = Math.floor(qrDataLength / 256);

            // QR Code commands for ESC/POS
            receipt += '\x1D\x28\x6B\x04\x00\x31\x41\x32\x00'; // Function 165: Select QR code model (Model 2)
            receipt += '\x1D\x28\x6B\x03\x00\x31\x43\x05'; // Function 167: Set module size (5)
            receipt += '\x1D\x28\x6B\x03\x00\x31\x45\x30'; // Function 169: Set error correction level (L = 48/0x30)
            receipt += '\x1D\x28\x6B' + String.fromCharCode(pL, pH) + '\x31\x50\x30' + qrCodeUrl; // Function 180: Store QR data
            receipt += '\x1D\x28\x6B\x03\x00\x31\x51\x30'; // Function 181: Print QR code

            receipt += '\n\n';
            receipt += qrCodeText + '\n';
        }

        receipt += '\x1B\x61\x00'; // Left align
        receipt += separator + '\n';

        // Feed and cut
        receipt += '\n\n\n\n';

        // Paper cut based on setting
        if (paperCut === 'full') {
            receipt += '\x1D\x56\x00'; // Full cut
        } else if (paperCut === 'partial') {
            receipt += '\x1D\x56\x41\x03'; // Partial cut
        }
        // If 'none', don't add any cut command

        return receipt;
    };

    // Print via Bluetooth using Web Bluetooth API
    const printViaBluetooth = async (receipt) => {
        if (!('bluetooth' in navigator)) {
            // Check if iOS device
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            if (isIOS) {
                setShowIosPrintOptions(true);
                throw new Error('iOS_BLUETOOTH_NOT_SUPPORTED');
            }
            throw new Error('Web Bluetooth API not supported. Please use Chrome, Edge, or Opera browser (version 89+) on a desktop or Android device.');
        }

        // Load saved printers from database to include their custom names in the filter
        let savedPrinters = [];
        try {
            const response = await fetch(`${BACKEND_URL}/api/bluetooth-printers`);
            if (response.ok) {
                savedPrinters = await response.json();
            }
        } catch (error) {
            console.warn('Could not load saved printers, using default filters:', error);
        }

        // Show printer mapping modal if there are saved printers and user hasn't chosen to skip
        if (savedPrinters.length > 0 && !skipPrinterMapping) {
            setPrinterMappings(savedPrinters);

            // Wait for user to close the modal
            await new Promise((resolve) => {
                printerMappingResolveRef.current = resolve;
                setShowPrinterMapping(true);
            });
        }

        // Build filters array with saved printer names and default fallbacks
        const filters = [
            { services: ['000018f0-0000-1000-8000-00805f9b34fb'] }, // ESC/POS service
        ];

        // Add filters for saved printers (using their original device names for pairing)
        savedPrinters.forEach(printer => {
            filters.push({ name: printer.device_name });
        });

        // Add default fallback filters
        filters.push(
            { name: 'GOOJPRT' },
            { namePrefix: 'PT' },
            { namePrefix: 'Printer' }
        );

        // Request Bluetooth device
        const device = await navigator.bluetooth.requestDevice({
            filters: filters,
            optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
        });

        // Connect to GATT server
        const server = await device.gatt.connect();

        // Get printer service (ESC/POS service UUID)
        const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');

        // Get write characteristic
        const characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');

        // Encode receipt data
        const encoder = new TextEncoder();
        const data = encoder.encode(receipt);

        // Send data in chunks (Bluetooth LE has packet size limits ~20 bytes)
        const chunkSize = 20;
        for (let i = 0; i < data.length; i += chunkSize) {
            const chunk = data.slice(i, i + chunkSize);
            await characteristic.writeValue(chunk);
            // Small delay between chunks
            await new Promise(resolve => setTimeout(resolve, 10));
        }

        // Disconnect
        device.gatt.disconnect();
    };

    // Print via iOS native print dialog (AirPrint)
    const handleIosPrint = async () => {
        try {
            setShowIosPrintOptions(false);
            setPrintLoading(true);
            await printViaIframe(printRef.current);
        } catch (error) {
            console.error('iOS print error:', error);
            alert(t('printError') + ': ' + error.message);
        } finally {
            setPrintLoading(false);
        }
    };

    // Generate and open receipt as PDF (for sharing/saving on iOS)
    const handleIosPdf = async () => {
        try {
            setPdfLoading(true);
            setShowIosPrintOptions(false);

            // Load printer settings
            let settings = null;
            try {
                const response = await fetch(`${BACKEND_URL}/api/printer-settings`);
                if (response.ok) {
                    settings = await response.json();
                }
            } catch (err) {
                console.warn('Could not load settings from API:', err);
            }

            if (!settings) {
                settings = {};
                try {
                    const savedFormat = localStorage.getItem('receiptFormat');
                    if (savedFormat) {
                        Object.assign(settings, JSON.parse(savedFormat));
                    }
                } catch (localErr) {
                    console.warn('Could not load from localStorage:', localErr);
                }
            }

            openReceiptPdf(orderItems, orderId, tableName, orderName, orderData?.special_name, settings, orderData?.closed_at, orderPayments);
        } catch (error) {
            console.error('PDF generation error:', error);
            alert(t('printError') + ': ' + error.message);
        } finally {
            setPdfLoading(false);
        }
    };

    // Helper to get option names from item.options (JSON string or array)
    const getOptionNames = (item, allOptions) => {
        if (!item.options) return null;
        let optionIds = [];
        if (typeof item.options === 'string') {
            try {
                optionIds = JSON.parse(item.options);
            } catch {
                return null;
            }
        } else if (Array.isArray(item.options)) {
            optionIds = item.options;
        }
        if (!Array.isArray(optionIds) || optionIds.length === 0) return null;
        if (!allOptions) return optionIds;
        // Filter out options with selected === 1
        return optionIds
            .map(id => allOptions.find(o => o.id === id))
            .filter(opt => opt && opt.selected !== 1)
            .map(opt => opt.name);
    };

    // Fetch all options for mapping ids to names
    const [allOptions, setAllOptions] = useState([]);
    useEffect(() => {
        fetch(`${BACKEND_URL}/api/debug/options`)
            .then(res => res.json())
            .then(data => setAllOptions(data))
            .catch(() => setAllOptions([]));
    }, []);

    if (loading) return <div style={{ color: theme?.colors?.textSecondary }}>{t('loading')} {t('orders').toLowerCase()} {t('items').toLowerCase()}...</div>;
    if (error) return <div style={{ color: 'red' }}>{t('error')}: {error}</div>;

    if (orderItems.length === 0) {
        const hasOpenChildren = childOrders.length > 0;

        return (
            <div style={{
                padding: '1rem',
                backgroundColor: theme?.colors?.surface || '#fff',
                borderRadius: theme?.borderRadius || 8,
                boxShadow: theme?.boxShadow,
                marginBottom: '1rem',
                textAlign: 'center'
            }}>
                <h4 style={{ color: theme?.colors?.primary, marginTop: 0 }}>
                    {t('emptyCheck')}
                    {parentOrderName && ` (${t('from')} ${parentOrderName})`}
                </h4>

                {hasOpenChildren ? (
                    <div style={{
                        padding: '1rem',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffeaa7',
                        borderRadius: theme?.borderRadius || 6,
                        color: '#856404',
                        marginBottom: '1rem'
                    }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                            ⚠️ {t('cannotCloseParentTab')}
                        </div>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                            {t('openChildrenTabs')}:
                        </div>
                        <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                            {childOrders.map(child => (
                                <li key={child.id} style={{ marginBottom: '0.25rem' }}>
                                    {child.name} ({child.table_name})
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : (
                    <button
                        onClick={async () => {
                            if (window.confirm(t('confirmCloseEmptyCheck'))) {
                                try {
                                    const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                            name: orderData?.name,
                                            table_id: orderData?.table_id,
                                            closed: 1,
                                            close_user_id: user?.id
                                        })
                                    });
                                    if (!response.ok) {
                                        const errorData = await response.json();
                                        if (response.status === 400 && errorData.children) {
                                            // Backend found open children, show them to user
                                            const childrenList = errorData.children.map(child => child.name).join(', ');
                                            alert(`${t('cannotCloseParentTab')}:\n${t('openChildrenTabs')}: ${childrenList}`);
                                            return;
                                        }
                                        throw new Error(errorData.error || 'Failed to close order');
                                    }
                                    onClose(); // Close the order view and return to table view
                                } catch (error) {
                                    console.error('Error closing order:', error);
                                    alert(t('error') + ': ' + error.message);
                                }
                            }
                        }}
                        style={{
                            backgroundColor: theme?.colors?.primary || '#1976d2',
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 6,
                            padding: '0.75rem 1.5rem',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {t('closeCheck')}
                    </button>
                )}
            </div>
        );
    }

    return (
        <div style={{
            padding: '0rem',
            backgroundColor: theme?.colors?.surface || '#fff',
            borderRadius: theme?.borderRadius || 8,
            boxShadow: theme?.boxShadow,
            marginBottom: '1rem'
        }}>
            {/* Items List - Limited to 3 items before scroll */}
            <div style={{
                maxHeight: '285px',
                overflowY: 'auto'
            }}>
                {sortedItems.map(item => (
                    <div
                        key={item.id}
                        style={{
                            marginBottom: '0.5rem',
                            padding: '0rem',
                            backgroundColor: item.status === 86 ? 'rgba(71, 22, 6, 0.1)' : theme?.colors?.background,
                            border: item.status === 86 ? '1px solid #ff5722' : '1px solid #ddd',
                            borderRadius: theme?.borderRadius || 6,
                            minWidth: 0,
                            overflow: 'hidden'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '0.5rem'
                        }}>
                            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                <div style={{
                                    color: theme?.colors?.text,
                                    fontWeight: 'bold',
                                    marginBottom: '0.25rem',
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word'
                                }}>
                                    {item.product_name} x{item.quantity} = ${(item.quantity * (Number(item.price || 0) - Number(item.discount || 0))).toFixed(2)}
                                    {item.discount !== 0 && item.discount !== "0.00" ? ` (-$${Number(item.discount).toFixed(2)}${item.discount_percent ? `/${item.discount_percent}%` : ''} ${t('discount')})` : ''}
                                </div>
                                {/* Option names below item name */}
                                {getOptionNames(item, allOptions) && getOptionNames(item, allOptions).length > 0 && (
                                    <div style={{ fontSize: '0.95em', color: theme?.colors?.secondary, marginBottom: '0.25rem' }}>
                                        {t('options')}: {getOptionNames(item, allOptions).join(', ')}
                                    </div>
                                )}
                                <div style={{
                                    fontSize: '0.85rem',
                                    color: getStatusColor(item.status),
                                    fontWeight: 'bold'
                                }}>
                                    {t('status')}: {getStatusText(item.status)}
                                </div>
                            </div>
                            <button
                                onClick={() => handleReorder(item)}
                                style={{
                                    marginLeft: '0.5rem',
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: theme?.colors?.secondary || '#666',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    fontWeight: 'bold'
                                }}
                                title={t('reorderThisItem')}
                            >
                                {t('reorder')}
                            </button>
                            <button
                                onClick={() => setEditingItem(item)}
                                style={{
                                    marginLeft: '0.5rem',
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    fontWeight: 'bold'
                                }}
                                title={t('editItem')}
                            >
                                {t('edit')}
                            </button>
                        </div>
                        {item.note && (
                            <div style={{
                                fontSize: '0.8rem',
                                color: '#666',
                                fontStyle: 'italic',
                                marginTop: '0.25rem'
                            }}>
                                {t('note')}: {item.note}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Specials Section */}
            {!checkingSpecials && (
                selectedSpecial ? (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '0.75rem',
                        backgroundColor: '#e8f5e9',
                        borderRadius: theme?.borderRadius || 6,
                        border: '2px solid #4caf50'
                    }}>
                        <h4 style={{
                            margin: '0 0 0.5rem 0',
                            color: '#2e7d32',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '0.95rem'
                        }}>
                            🎁 {t('specialApplied')}
                        </h4>
                        <div>
                            <div style={{
                                padding: '0.5rem',
                                backgroundColor: '#fff',
                                borderRadius: theme?.borderRadius || 4,
                                marginBottom: '0.5rem',
                                border: '2px solid #4caf50'
                            }}>
                                <div style={{ fontWeight: 'bold', color: '#2e7d32', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                                    {selectedSpecial.name}
                                </div>
                                {getSpecialDescription(selectedSpecial) && (
                                    <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
                                        {getSpecialDescription(selectedSpecial)}
                                    </div>
                                )}
                                <div style={{ fontSize: '0.85rem', color: '#2e7d32', fontWeight: 'bold' }}>
                                    {t('discount')}: -${specialDiscount.toFixed(2)}
                                </div>
                            </div>
                            <button
                                onClick={removeSpecial}
                                style={{
                                    padding: '0.4rem 0.75rem',
                                    backgroundColor: '#f44336',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    width: '100%'
                                }}
                            >
                                {t('remove')} {t('special')}
                            </button>
                        </div>
                    </div>
                ) : (
                    applicableSpecials.length > 0 && (
                        <div style={{
                            marginBottom: '1rem',
                            padding: '0.75rem',
                            backgroundColor: '#e8f5e9',
                            borderRadius: theme?.borderRadius || 6,
                            border: '2px solid #4caf50'
                        }}>
                            <h4
                                onClick={() => setShowSpecials(!showSpecials)}
                                style={{
                                    margin: '0 0 0.5rem 0',
                                    color: '#2e7d32',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '0.5rem',
                                    fontSize: '0.95rem',
                                    cursor: 'pointer',
                                    userSelect: 'none'
                                }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    🎁 {t('specialsAvailable')}
                                </span>
                                <span style={{ fontSize: '1.2rem' }}>
                                    {showSpecials ? '▼' : '▶'}
                                </span>
                            </h4>
                            {showSpecials && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                    {applicableSpecials.map(special => (
                                        <button
                                            key={special.id}
                                            onClick={() => applySpecial(special)}
                                            style={{
                                                padding: '0.5rem',
                                                backgroundColor: '#fff',
                                                color: '#2e7d32',
                                                border: `0.5px solid ${theme?.colors?.primary || '#4caf50'}`,
                                                borderRadius: theme?.borderRadius || 4,
                                                cursor: 'pointer',
                                                fontSize: '0.8rem',
                                                textAlign: 'left',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.backgroundColor = '#f1f8f4';
                                                e.currentTarget.style.borderWidth = '2px';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.backgroundColor = '#fff';
                                                e.currentTarget.style.borderWidth = '1px';
                                            }}
                                        >
                                            <div style={{ fontWeight: 'bold', marginBottom: '0.15rem' }}>
                                                {special.name}
                                            </div>
                                            {getSpecialDescription(special) && (
                                                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                                    {getSpecialDescription(special)}
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                )
            )}

            <div style={{
                marginBottom: '1rem',
                paddingBottom: '1rem',
                borderBottom: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '1rem'
                }}>
                    <div style={{
                        fontSize: '1.2rem',
                        color: theme?.colors?.primary || '#1976d2',
                        flex: '0 0 auto'
                    }}>
                        <span style={{ fontWeight: 'normal', fontSize: '0.9rem', lineHeight: '1.6' }}>
                            {t('subtotal')}: ${splitTaxInclusive(totalPrice, taxRate).subtotal.toFixed(2)} <br />
                            {t('discount')}: -${totalDiscount.toFixed(2)} <br />
                            {t('iva')}({formatTaxRate(taxRate)}%): ${splitTaxInclusive(totalPrice, taxRate).tax.toFixed(2)} <br />
                            <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                {t('total')}: ${totalPrice.toFixed(2)}
                            </span>
                        </span>
                    </div>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        flex: '1 1 auto',
                        alignItems: 'flex-end'
                    }}>
                        {/* Preview and Print buttons row */}
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowPrintTicket(!showPrintTicket)}
                                style={{
                                    backgroundColor: theme?.colors?.secondary || '#666',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    padding: '0.5rem 1rem',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold'
                                }}
                                title={showPrintTicket ? t('hidePrintPreview') : t('showPrintPreview')}
                            >
                                👁️ {showPrintTicket ? t('hide') : t('preview')}
                            </button>
                            <button
                                onClick={handlePrint2}
                                disabled={printLoading}
                                style={{
                                    backgroundColor: printLoading ? '#ccc' : theme?.colors?.primary || '#1976d2',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    padding: '0.5rem 1rem',
                                    cursor: printLoading ? 'not-allowed' : 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}
                                title={t('printReceipt')}
                            >
                                🖨️ {printLoading ? t('printing') : t('print')}
                            </button>
                        </div>
                        {/* Split, Combine, Close buttons row */}
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {orderData?.parent_tab_id ? (
                        // Show recombine button for split checks
                        <button
                            onClick={() => setShowRecombineCheck(true)}
                            style={{
                                backgroundColor: '#ff9800',
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 4,
                                padding: '0.5rem 1rem',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: 'bold'
                            }}
                            title={t('recombineThisSplitCheckWithItsParent')}
                        >
                            {t('recombine')}
                        </button>
                    ) : (
                        // Show split button for regular checks
                        <button
                            onClick={() => setShowSplitCheck(true)}
                            style={{
                                backgroundColor: theme?.colors?.primary || '#1976d2',
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 4,
                                padding: '0.5rem 1rem',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: 'bold'
                            }}
                            title={t('splitThisCheck')}
                        >
                            {t('split')}
                        </button>
                    )}
                    <button
                        onClick={() => setShowCombineCheck(true)}
                        style={{
                            backgroundColor: theme?.colors?.secondary || '#666',
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 4,
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: 'bold'
                        }}
                        title={t('combineThisCheckWithAnotherCheck')}
                    >
                        {t('combine')}
                    </button>
                    <button
                        onClick={() => setShowOrderClose(true)}
                        style={{
                            backgroundColor: theme?.colors?.error || '#d32f2f',
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 4,
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: 'bold'
                        }}
                        title={t('closeThisCheck')}
                    >
                        {t('close')}
                    </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Print Preview Section */}
            {showPrintTicket && (
                <div style={{
                    marginBottom: '1rem',
                    padding: '1rem',
                    backgroundColor: theme?.colors?.background || '#f5f5f5',
                    borderRadius: theme?.borderRadius || 8,
                    border: '2px dashed #ccc'
                }}>
                    <h4 style={{
                        margin: '0 0 1rem 0',
                        color: theme?.colors?.primary,
                        textAlign: 'center'
                    }}>
                        {t('printPreview')}
                    </h4>
                    <PrintTicket
                        ref={printRef}
                        items={orderItems}
                        orderId={orderId}
                        table={{ name: tableName }}
                        tab={{ name: orderName }}
                        theme={theme}
                        allOptions={allOptions}
                        special={selectedSpecial}
                        closedAt={orderData?.closed_at}
                        payments={orderPayments}
                    />
                </div>
            )}

            {/* Hidden PrintTicket for printing when preview is hidden */}
            {!showPrintTicket && (
                <div style={{ display: 'none' }}>
                    <PrintTicket
                        ref={printRef}
                        items={orderItems}
                        orderId={orderId}
                        table={{ name: tableName }}
                        tab={{ name: orderName }}
                        theme={theme}
                        allOptions={allOptions}
                        special={selectedSpecial}
                        closedAt={orderData?.closed_at}
                        payments={orderPayments}
                    />
                </div>
            )}

            {editingItem && (
                <EditItemCheck
                    item={editingItem}
                    theme={theme}
                    onClose={() => setEditingItem(null)}
                    onSave={() => {
                        setEditingItem(null);
                        fetchOrderItems();
                    }}
                />
            )}

            {showPrinterSettings && (
                <PrinterSettings onClose={() => setShowPrinterSettings(false)} />
            )}

            {showOrderClose && (
                <OrderClose
                    orderId={orderId}
                    orderItems={orderItems}
                    totalPrice={totalPrice}
                    tableName={tableName}
                    orderName={orderName}
                    theme={theme}
                    user={user}
                    onClose={() => setShowOrderClose(false)}
                    onOrderClosed={() => {
                        setShowOrderClose(false);
                        // Refresh the order items to reflect the closed state
                        fetchOrderItems();
                        // Return to main dashboard
                        if (onClose) {
                            onClose();
                        }
                    }}
                />
            )}

            {showSplitCheck && (
                    <SplitCheck
                        orderId={orderId}
                        orderItems={orderItems}
                        totalPrice={totalPrice}
                        tableName={tableName}
                        orderName={orderName}
                        theme={theme}
                        user={user}
                        setShowSplitCheck={setShowSplitCheck}
                        onClose={() => setShowSplitCheck(false)}
                        onSplitCompleted={() => {
                            setShowSplitCheck(false);
                            // Refresh the order items to reflect the split
                            fetchOrderItems();
                        }}
                    />
            )}

            {showRecombineCheck && (
                <RecombineCheck
                    orderId={orderId}
                    orderItems={orderItems}
                    orderData={orderData}
                    totalPrice={totalPrice}
                    tableName={tableName}
                    orderName={orderName}
                    theme={theme}
                    user={user}
                    onClose={() => setShowRecombineCheck(false)}
                    onRecombineCompleted={() => {
                        setShowRecombineCheck(false);
                        // Navigate back to main dashboard since this split check will be deleted
                        if (onClose) {
                            onClose();
                        }
                    }}
                />
            )}

            {showCombineCheck && (
                <CombineCheck
                    orderId={orderId}
                    orderItems={orderItems}
                    orderData={orderData}
                    totalPrice={totalPrice}
                    tableName={tableName}
                    orderName={orderName}
                    theme={theme}
                    user={user}
                    onClose={() => setShowCombineCheck(false)}
                    onCombineCompleted={() => {
                        setShowCombineCheck(false);
                        // Refresh the order items to reflect the combined items
                        fetchOrderItems();
                        fetchOrderData();
                    }}
                />
            )}

            {/* iOS Print Options Modal */}
            {showIosPrintOptions && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.text || '#000',
                        padding: '2rem',
                        borderRadius: theme?.borderRadius || '12px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                    }}>
                        <h2 style={{
                            marginTop: 0,
                            color: theme?.colors?.primary || '#1976d2',
                            fontSize: '1.5rem'
                        }}>
                            🖨️ {t('iosPrintOptions')}
                        </h2>
                        <p style={{ fontSize: '1rem', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                            {t('iosPrintHelp')}
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                            <button
                                onClick={handleIosPrint}
                                disabled={printLoading}
                                style={{
                                    padding: '1rem',
                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || '8px',
                                    cursor: printLoading ? 'not-allowed' : 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: '1rem'
                                }}
                            >
                                🖨️ {t('printAirprint')}
                            </button>
                            <button
                                onClick={handleIosPdf}
                                disabled={pdfLoading}
                                style={{
                                    padding: '1rem',
                                    backgroundColor: theme?.colors?.secondary || '#4caf50',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || '8px',
                                    cursor: pdfLoading ? 'not-allowed' : 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: '1rem'
                                }}
                            >
                                📄 {pdfLoading ? t('generatingPdf') : t('shareAsPdf')}
                            </button>
                            <a
                                href="https://apps.apple.com/app/bluefy-web-ble-browser/id1492822055"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    padding: '0.5rem',
                                    textAlign: 'center',
                                    color: theme?.colors?.textSecondary || '#666',
                                    fontSize: '0.85rem',
                                    textDecoration: 'underline'
                                }}
                            >
                                {t('bluetoothAlternative')}
                            </a>
                            <button
                                onClick={() => setShowIosPrintOptions(false)}
                                style={{
                                    padding: '0.75rem',
                                    backgroundColor: theme?.colors?.border || '#ccc',
                                    color: theme?.colors?.text || '#000',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || '8px',
                                    cursor: 'pointer',
                                    fontSize: '1rem'
                                }}
                            >
                                {t('close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Printer Mapping Modal */}
            {showPrinterMapping && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.text || '#000',
                        padding: '2rem',
                        borderRadius: theme?.borderRadius || '12px',
                        maxWidth: '600px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                    }}>
                        <h2 style={{
                            marginTop: 0,
                            color: theme?.colors?.primary || '#1976d2',
                            fontSize: '1.5rem',
                            marginBottom: '1rem'
                        }}>
                            🖨️ {t('printerMapping') || 'Printer Quick Reference'}
                        </h2>

                        <p style={{
                            fontSize: '1rem',
                            lineHeight: '1.6',
                            marginBottom: '1.5rem',
                            color: theme?.colors?.textSecondary || '#666'
                        }}>
                            {t('printerMappingHelp') || 'The Bluetooth pairing dialog will show the original device names. Use this reference to identify your printers:'}
                        </p>

                        <div style={{
                            backgroundColor: theme?.colors?.background || '#f5f5f5',
                            borderRadius: theme?.borderRadius || '8px',
                            padding: '1rem',
                            marginBottom: '1.5rem'
                        }}>
                            {printerMappings.map((printer, index) => (
                                <div key={printer.id} style={{
                                    padding: '1rem',
                                    backgroundColor: theme?.colors?.surface || '#fff',
                                    borderRadius: theme?.borderRadius || '6px',
                                    marginBottom: index < printerMappings.length - 1 ? '0.75rem' : 0,
                                    border: `2px solid ${theme?.colors?.primary || '#1976d2'}`
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem',
                                        flexWrap: 'wrap'
                                    }}>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{
                                                fontSize: '0.85rem',
                                                color: theme?.colors?.textSecondary || '#666',
                                                marginBottom: '0.25rem'
                                            }}>
                                                {t('selectThisDevice') || 'Select this device:'}
                                            </div>
                                            <div style={{
                                                fontSize: '1.1rem',
                                                fontWeight: 'bold',
                                                color: theme?.colors?.text || '#000',
                                                fontFamily: 'monospace'
                                            }}>
                                                {printer.device_name}
                                            </div>
                                        </div>
                                        <div style={{
                                            fontSize: '1.5rem',
                                            color: theme?.colors?.textSecondary || '#666'
                                        }}>
                                            →
                                        </div>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{
                                                fontSize: '0.85rem',
                                                color: theme?.colors?.textSecondary || '#666',
                                                marginBottom: '0.25rem'
                                            }}>
                                                {t('customName') || 'Your custom name:'}
                                            </div>
                                            <div style={{
                                                fontSize: '1.1rem',
                                                fontWeight: 'bold',
                                                color: theme?.colors?.primary || '#1976d2'
                                            }}>
                                                {printer.custom_name}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '1rem',
                            backgroundColor: theme?.colors?.background || '#f5f5f5',
                            borderRadius: theme?.borderRadius || '8px',
                            marginBottom: '1.5rem'
                        }}>
                            <input
                                type="checkbox"
                                id="skipPrinterMapping"
                                checked={skipPrinterMapping}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setSkipPrinterMapping(checked);
                                    localStorage.setItem('skipPrinterMapping', checked.toString());
                                }}
                                style={{
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer'
                                }}
                            />
                            <label
                                htmlFor="skipPrinterMapping"
                                style={{
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    userSelect: 'none'
                                }}
                            >
                                {t('dontShowAgain') || "Don't show this again"}
                            </label>
                        </div>

                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <button
                                onClick={() => {
                                    setShowPrinterMapping(false);
                                    // Resolve the promise to continue with Bluetooth pairing
                                    if (printerMappingResolveRef.current) {
                                        printerMappingResolveRef.current();
                                        printerMappingResolveRef.current = null;
                                    }
                                }}
                                style={{
                                    flex: 1,
                                    padding: '1rem',
                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || '8px',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                {t('gotIt') || 'Got It! Proceed to Pair'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default OrderTotal;